import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Tag, ToggleLeft, ToggleRight, AlertCircle, Megaphone, Sparkles, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GRADIENT_PRESETS = [
  { id: 'summer', label: 'Summer', from: '#e11d48', via: '#ea580c', to: '#d97706' },
  { id: 'spring', label: 'Spring', from: '#059669', via: '#10b981', to: '#34d399' },
  { id: 'autumn', label: 'Autumn', from: '#c2410c', via: '#b45309', to: '#92400e' },
  { id: 'winter', label: 'Winter', from: '#1d4ed8', via: '#6366f1', to: '#7c3aed' },
  { id: 'christmas', label: 'Christmas', from: '#dc2626', via: '#16a34a', to: '#dc2626' },
  { id: 'blackfriday', label: 'Black Friday', from: '#0a0a0a', via: '#374151', to: '#0a0a0a' },
  { id: 'valentines', label: 'Valentine\'s', from: '#ec4899', via: '#f43f5e', to: '#e11d48' },
  { id: 'newyear', label: 'New Year', from: '#7c3aed', via: '#c026d3', to: '#e11d48' },
  { id: 'flash', label: 'Flash Sale', from: '#facc15', via: '#f97316', to: '#ef4444' },
  { id: 'ocean', label: 'Ocean', from: '#0891b2', via: '#0284c7', to: '#1d4ed8' },
];

function gradientStyle(banner) {
  if (banner.gradient_type === 'custom' && banner.gradient_from && banner.gradient_to) {
    return { background: `linear-gradient(to right, ${banner.gradient_from}, ${banner.gradient_via || banner.gradient_to}, ${banner.gradient_to})` };
  }
  const preset = GRADIENT_PRESETS.find(p => p.id === banner.gradient_type);
  if (preset) {
    return { background: `linear-gradient(to right, ${preset.from}, ${preset.via}, ${preset.to})` };
  }
  return { background: `linear-gradient(to right, ${GRADIENT_PRESETS[0].from}, ${GRADIENT_PRESETS[0].via}, ${GRADIENT_PRESETS[0].to})` };
}

function SaleBannerEditor() {
  const [banner, setBanner] = useState({ active: false, name: '', description: '', discount_text: '', gradient_type: 'summer', gradient_from: '#e11d48', gradient_via: '#ea580c', gradient_to: '#d97706' });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetch(`${API}/api/shop/sale-banner`)
      .then(r => r.json())
      .then(d => { setBanner(prev => ({ ...prev, ...d })); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/api/admin/shop/sale-banner`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(banner),
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    const updated = { ...banner, active: !banner.active };
    setBanner(updated);
    try {
      await fetch(`${API}/api/admin/shop/sale-banner`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updated),
      });
    } catch (err) {
      console.error(err);
    }
  };

  const selectPreset = (presetId) => {
    const preset = GRADIENT_PRESETS.find(p => p.id === presetId);
    if (preset) {
      setBanner(b => ({ ...b, gradient_type: presetId, gradient_from: preset.from, gradient_via: preset.via, gradient_to: preset.to }));
    }
  };

  if (!loaded) return null;

  const bannerGradient = gradientStyle(banner);

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-amber-500 flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-['Outfit'] text-lg font-bold text-white">Sale Banner</h2>
            <p className="text-xs text-slate-500">Display a promotional ribbon across the shop</p>
          </div>
        </div>
        <button onClick={toggleActive} data-testid="sale-banner-toggle"
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold border transition-all ${
            banner.active
              ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
              : 'border-white/10 text-slate-500 hover:text-white hover:border-white/20'
          }`}>
          {banner.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
          {banner.active ? 'LIVE' : 'OFF'}
        </button>
      </div>

      <div className={`border rounded-2xl p-5 space-y-4 transition-colors ${banner.active ? 'border-emerald-500/20 bg-emerald-500/[0.03]' : 'border-white/10 bg-white/[0.02]'}`}>
        {/* Preview */}
        {(banner.name || banner.discount_text) && (
          <div className="rounded-xl overflow-hidden">
            <p className="text-[10px] font-medium text-slate-500 mb-1.5 uppercase tracking-widest">Preview</p>
            <div className="relative overflow-hidden py-2.5 rounded-lg" style={bannerGradient} data-testid="sale-banner-preview">
              <div className="sale-marquee flex whitespace-nowrap">
                {[...Array(8)].map((_, i) => (
                  <span key={i} className="inline-flex items-center gap-3 mx-6 text-white text-sm font-bold uppercase tracking-wider">
                    <Sparkles className="w-3.5 h-3.5" />
                    {banner.discount_text && <span>{banner.discount_text}</span>}
                    {banner.name && <span className="font-black">{banner.name}</span>}
                    {banner.description && <span className="font-normal opacity-90">{banner.description}</span>}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Event / Sale Name</label>
            <input data-testid="sale-banner-name" value={banner.name} onChange={e => setBanner(b => ({ ...b, name: e.target.value }))}
              placeholder="e.g. SUMMER SALE" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm uppercase tracking-wider placeholder:text-slate-600 focus:outline-none focus:border-[#00E5FF]/50 font-semibold" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Discount Amount</label>
            <input data-testid="sale-banner-discount" value={banner.discount_text} onChange={e => setBanner(b => ({ ...b, discount_text: e.target.value }))}
              placeholder="e.g. 20% OFF" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-[#00E5FF]/50" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-400 block mb-1.5">Short Description</label>
          <input data-testid="sale-banner-description" value={banner.description} onChange={e => setBanner(b => ({ ...b, description: e.target.value }))}
            placeholder="e.g. Use code SUMMER20 at checkout" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-[#00E5FF]/50" />
        </div>

        {/* Gradient Theme Selector */}
        <div>
          <label className="text-xs font-medium text-slate-400 block mb-2">Banner Theme</label>
          <div className="grid grid-cols-5 sm:grid-cols-10 gap-2" data-testid="gradient-presets">
            {GRADIENT_PRESETS.map(preset => (
              <button key={preset.id} type="button" onClick={() => selectPreset(preset.id)}
                title={preset.label}
                data-testid={`gradient-${preset.id}`}
                className={`relative h-8 rounded-lg transition-all ${banner.gradient_type === preset.id ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0A0A0A] scale-110' : 'hover:scale-105 opacity-75 hover:opacity-100'}`}
                style={{ background: `linear-gradient(to right, ${preset.from}, ${preset.via}, ${preset.to})` }}>
                {banner.gradient_type === preset.id && (
                  <Check className="w-3.5 h-3.5 text-white absolute inset-0 m-auto drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" />
                )}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {GRADIENT_PRESETS.map(preset => (
              <button key={preset.id} type="button" onClick={() => selectPreset(preset.id)}
                className={`text-[9px] px-2 py-0.5 rounded-full transition-colors ${banner.gradient_type === preset.id ? 'bg-white/15 text-white font-semibold' : 'text-slate-600 hover:text-slate-400'}`}>
                {preset.label}
              </button>
            ))}
          </div>
        </div>

        {/* Custom Gradient */}
        <div>
          <button type="button" onClick={() => setBanner(b => ({ ...b, gradient_type: 'custom' }))}
            data-testid="gradient-custom-toggle"
            className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${banner.gradient_type === 'custom' ? 'border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF]' : 'border-white/10 text-slate-500 hover:text-white hover:border-white/20'}`}>
            Custom Gradient
          </button>
          {banner.gradient_type === 'custom' && (
            <div className="flex items-center gap-3 mt-3">
              <div className="flex items-center gap-2">
                <label className="text-[10px] text-slate-500">From</label>
                <input type="color" value={banner.gradient_from || '#e11d48'} onChange={e => setBanner(b => ({ ...b, gradient_from: e.target.value }))}
                  data-testid="gradient-from" className="w-8 h-8 rounded-lg border border-white/10 cursor-pointer bg-transparent p-0.5" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] text-slate-500">Mid</label>
                <input type="color" value={banner.gradient_via || '#ea580c'} onChange={e => setBanner(b => ({ ...b, gradient_via: e.target.value }))}
                  data-testid="gradient-via" className="w-8 h-8 rounded-lg border border-white/10 cursor-pointer bg-transparent p-0.5" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] text-slate-500">To</label>
                <input type="color" value={banner.gradient_to || '#d97706'} onChange={e => setBanner(b => ({ ...b, gradient_to: e.target.value }))}
                  data-testid="gradient-to" className="w-8 h-8 rounded-lg border border-white/10 cursor-pointer bg-transparent p-0.5" />
              </div>
              <div className="flex-1 h-6 rounded-md" style={{ background: `linear-gradient(to right, ${banner.gradient_from}, ${banner.gradient_via || banner.gradient_to}, ${banner.gradient_to})` }} />
            </div>
          )}
        </div>

        <button onClick={handleSave} disabled={saving} data-testid="sale-banner-save"
          className="inline-flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-white transition-colors disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Banner'}
        </button>
      </div>
    </div>
  );
}

// Export for ShopPage to use
export { GRADIENT_PRESETS, gradientStyle };

export default function DiscountManager() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', percentage: '', min_order: '', usage_limit: '', limitType: 'unlimited' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const token = localStorage.getItem('admin_token');

  const fetchCodes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/discounts`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setCodes(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.percentage) { setError('Code and percentage are required'); return; }
    setSaving(true);
    setError('');
    try {
      const body = {
        code: form.code.trim(),
        percentage: parseFloat(form.percentage),
        min_order: form.min_order ? parseFloat(form.min_order) : null,
        usage_limit: form.limitType === 'limited' && form.usage_limit ? parseInt(form.usage_limit) : null,
      };
      const res = await fetch(`${API}/api/admin/discounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || 'Failed'); }
      setForm({ code: '', percentage: '', min_order: '', usage_limit: '', limitType: 'unlimited' });
      setShowForm(false);
      fetchCodes();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this discount code?')) return;
    await fetch(`${API}/api/admin/discounts/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchCodes();
  };

  const handleToggle = async (id) => {
    await fetch(`${API}/api/admin/discounts/${id}/toggle`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
    fetchCodes();
  };

  return (
    <div>
      {/* Sale Banner Editor */}
      <SaleBannerEditor />

      {/* Discount Codes Section */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-white">Discount Codes</h1>
          <p className="text-sm text-slate-500 mt-0.5">{codes.length} code{codes.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="add-discount-btn"
          className="flex items-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-white transition-colors">
          <Plus className="w-4 h-4" /> New Code
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-6 space-y-4" data-testid="discount-form">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-1.5">Code</label>
              <input data-testid="discount-code-input" type="text" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. SUMMER20" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm uppercase tracking-wider placeholder:text-slate-600 focus:outline-none focus:border-[#00E5FF]/50 font-mono" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-1.5">Percentage Off</label>
              <div className="relative">
                <input data-testid="discount-percent-input" type="number" min="1" max="100" value={form.percentage} onChange={e => setForm(f => ({ ...f, percentage: e.target.value }))}
                  placeholder="10" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 pr-8 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-[#00E5FF]/50" />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-400 block mb-1.5">Minimum Order Value (optional)</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
              <input data-testid="discount-min-order-input" type="number" min="0" step="0.01" value={form.min_order} onChange={e => setForm(f => ({ ...f, min_order: e.target.value }))}
                placeholder="No minimum" className="w-full bg-white/5 border border-white/10 rounded-xl pl-7 pr-4 py-3 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-[#00E5FF]/50" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-400 block mb-2">Usage Limit</label>
            <div className="flex gap-3">
              <button type="button" onClick={() => setForm(f => ({ ...f, limitType: 'unlimited' }))}
                className={`px-4 py-2 rounded-lg text-xs font-medium border transition-colors ${form.limitType === 'unlimited' ? 'border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]' : 'border-white/10 text-slate-400 hover:border-white/20'}`}>
                Unlimited
              </button>
              <button type="button" onClick={() => setForm(f => ({ ...f, limitType: 'limited' }))}
                className={`px-4 py-2 rounded-lg text-xs font-medium border transition-colors ${form.limitType === 'limited' ? 'border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]' : 'border-white/10 text-slate-400 hover:border-white/20'}`}>
                Set Limit
              </button>
            </div>
            {form.limitType === 'limited' && (
              <input data-testid="discount-usage-limit-input" type="number" min="1" value={form.usage_limit} onChange={e => setForm(f => ({ ...f, usage_limit: e.target.value }))}
                placeholder="Max uses" className="mt-2 w-40 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-[#00E5FF]/50" />
            )}
          </div>

          {error && (
            <div className="flex items-center gap-2 text-xs text-rose-400 bg-rose-500/10 rounded-lg px-3 py-2">
              <AlertCircle className="w-3.5 h-3.5" /> {error}
            </div>
          )}

          <div className="flex gap-2">
            <button type="submit" disabled={saving} data-testid="save-discount-btn"
              className="bg-[#00E5FF] text-[#0A0A0A] px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-white transition-colors disabled:opacity-50">
              {saving ? 'Creating...' : 'Create Code'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); setError(''); }}
              className="text-sm text-slate-500 hover:text-white transition-colors px-4">Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-16">
          <div className="w-8 h-8 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        </div>
      ) : codes.length === 0 ? (
        <div className="text-center py-16 bg-white/[0.02] border border-white/5 rounded-2xl">
          <Tag className="w-8 h-8 text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-slate-500">No discount codes yet</p>
          <p className="text-xs text-slate-600 mt-1">Create your first code above</p>
        </div>
      ) : (
        <div className="space-y-2">
          {codes.map(code => (
            <div key={code.id} data-testid={`discount-row-${code.code}`}
              className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${code.active ? 'bg-white/[0.02] border-white/10' : 'bg-white/[0.01] border-white/5 opacity-60'}`}>
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0">
                  <Tag className="w-4 h-4 text-[#00E5FF]" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm font-bold text-white tracking-wider">{code.code}</span>
                    <span className="text-xs font-semibold text-[#00E5FF] bg-[#00E5FF]/10 px-2 py-0.5 rounded-full">{code.percentage}% off</span>
                    {!code.active && <span className="text-[10px] font-medium text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded-full">Disabled</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                    {code.min_order ? <span>Min: £{code.min_order.toFixed(2)}</span> : <span>No minimum</span>}
                    <span>·</span>
                    {code.usage_limit ? <span>{code.times_used}/{code.usage_limit} used</span> : <span>{code.times_used} used · Unlimited</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => handleToggle(code.id)} data-testid={`toggle-discount-${code.code}`}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-[#00E5FF] hover:bg-white/5 transition-colors">
                  {code.active ? <ToggleRight className="w-5 h-5 text-[#00E5FF]" /> : <ToggleLeft className="w-5 h-5" />}
                </button>
                <button onClick={() => handleDelete(code.id)} data-testid={`delete-discount-${code.code}`}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
